
	import { Component, Prop, Provide, Mixins, Watch } from 'vue-property-decorator';
	import AvisoAlCerrar from '@/mixins/AvisoAlCerrar';
	import AvisoSinGuardar from '@/components/core/AvisoSinGuardar.vue';
	import { ValidationProvider, ValidationObserver } from 'vee-validate';
	import ApiHelper from '@/helpers/api-helper';
	import CargandoFicha from '@/components/core/CargandoFicha.vue';
	import PackageService from '@/services/PackageService';
	import TravelSelect from '@/components/inputs/TravelSelect.vue';
	import TabTravelPackage from '@/components/inputs/TabTravelPackage.vue';
	import TabAccommodationPackage from '@/components/inputs/TabAccommodationPackage.vue';
	import TabTransportPackage from '@/components/inputs/TabTransportPackage.vue';
	import TabExtraServicePackage from '@/components/inputs/TabExtraServicePackage.vue';
	import TabActivityPackage from '@/components/inputs/TabActivityPackage.vue';
	import Package from '@/models/package';
	import FormatHelper from '@/helpers/format-helper';
	import { ESPERA_DE_PRECIOS, EsperaDePrecios, nuevaEspera, textoDeEspera } from '@/helpers/espera-de-precios';

	/**
	 * N-291 (T5b): lo que la pestaña «Viaje» lleva de vuelta al paquete al validar. Tiene que coincidir con
	 * `setTravelPackage` (que sigue campo a campo: lo ata `PackageSeoTexts.spec.js`).
	 */
	const CAMPOS_DEL_VIAJE = [
		'name',
		'description',
		'img_url',
		'status_id',
		'travel_id',
		'days',
		'offer',
		'offer_start_date',
		'offer_end_date',
		'offer_amount',
		'people',
		'text_1',
		'text_2',
		'text_3',
	];

	@Component({
		components: {
			AvisoSinGuardar,
			CargandoFicha,
			TabTravelPackage,
			TabAccommodationPackage,
			TabTransportPackage,
			TabExtraServicePackage,
			TabActivityPackage,
			TravelSelect,
			ValidationProvider,
			ValidationObserver,
		},
	})
	export default class TravelForm extends Mixins(AvisoAlCerrar) {
		@Prop({ default: null, required: true }) private edit_id;

		$refs!: {
			obs: InstanceType<typeof ValidationObserver>;
			tabTravelPackage: InstanceType<typeof TabTravelPackage>;
			tabAccommodationPackage: InstanceType<typeof TabAccommodationPackage>;
			tabTransportPackage: InstanceType<typeof TabTransportPackage>;
			tabExtraServicePackage: InstanceType<typeof TabExtraServicePackage>;
			tabActivityPackage: InstanceType<typeof TabActivityPackage>;
		};

		private loading = true;
		private fotoTrasLosCatalogos = false;
		private cargaFallida = false;
		private formTitle = '';
		private package_id: any = null;
		private package: Package = new Package();
		private activeTab = 0;
		private travel_id: any;
		private total: any;
		private people: any;
		// N-288 · 4A (también en paquetes, 8-oct): los catálogos de precios en camino; cada pestaña la
		// recoge con `inject`.
		@Provide(ESPERA_DE_PRECIOS) private esperaDePrecios: EsperaDePrecios = nuevaEspera();

		constructor() {
			super();
			this.travel_id = null;
			this.people = 0;
			this.total = {
				accommodation_price: 0,
				transport_price: 0,
				extra_service_price: 0,
				activity_price: 0,
			};
		}

		/**
		 * N-291 (T5b): lo que GUARDAR mandaría. Las cinco pestañas trabajan sobre su copia y solo la vuelcan al validar
		 * (`validateTab`): se superpone lo que cada una lleva ahora.
		 */
		loQueSeGuardaria() {
			const pestanas: any = this.$refs;
			const paquete: any = { ...this.package };
			if (pestanas.tabTravelPackage && pestanas.tabTravelPackage.packageData) {
				CAMPOS_DEL_VIAJE.forEach(campo => {
					paquete[campo] = pestanas.tabTravelPackage.packageData[campo];
				});
			}
			if (pestanas.tabAccommodationPackage) {
				paquete.accommodation = pestanas.tabAccommodationPackage.packageAccommodationData;
			}
			if (pestanas.tabTransportPackage) {
				paquete.transport = pestanas.tabTransportPackage.packageTransportData;
			}
			if (pestanas.tabExtraServicePackage) {
				paquete.extra_service = pestanas.tabExtraServicePackage.packageExtraServiceData;
			}
			if (pestanas.tabActivityPackage) {
				paquete.activities = pestanas.tabActivityPackage.daysData;
			}
			return JSON.stringify(paquete);
		}

		/**
		 * N-291 (T5b): al leer sus catálogos, las pestañas de alojamiento y actividades COMPLETAN su copia solas (medido en
		 * la réplica: precio y nombre del alojamiento, detalles de las actividades, mientras la espera de precios baja a 0).
		 * Eso no es un cambio del operador: la foto se rehace la primera vez que la espera vuelve a 0 (como la ficha del
		 * presupuesto, `alLlegarLosCatalogos`).
		 */
		@Watch('esperaDePrecios.enCamino')
		alLlegarLosCatalogos(ahora: number, antes: number) {
			if (this.fotoTrasLosCatalogos || this.alCargar === null || ahora !== 0 || !(antes > 0)) {
				return;
			}
			this.fotoTrasLosCatalogos = true;
			this.fotoSinCambios();
		}

		mounted() {
			this.package_id = this.edit_id;
			if (this.package_id) {
				this.loading = true;
				this.formTitle = 'Edit Package';
				PackageService.get(this.package_id)
					.then(res => {
						if (res.status === 200) {
							this.package = res.data;
							this.travel_id = this.package['travel_id'];
							this.people = this.package['people'];
							this.loading = false;
							this.fotoSinCambios();
						}
					})
					.catch(error => {
						this.cargaFallida = true;
						ApiHelper.handleErrors(error, {}, this.$bus);
					});
			} else {
				this.formTitle = 'New Package';
				this.fotoSinCambios();
				this.loading = false;
			}
		}

		async create() {
			const component = this;
			const valid = await this.validateAllTab();
			if (valid) {
				PackageService.create(this.package)
					.then(res => {
						if (res.status === 201) {
							this.$emit('saved');
						}
					})
					.catch(error => {
						ApiHelper.handleErrors(error, component.$refs['obs'], this.$bus);
					});
			}
		}

		async update() {
			const component = this;
			const valid = await this.validateAllTab();
			if (valid) {
				PackageService.update(this.package)
					.then(res => {
						if (res.status === 200) {
							this.$emit('saved');
						}
					})
					.catch(error => {
						ApiHelper.handleErrors(error, component.$refs['obs'], this.$bus);
					});
			}
		}

		async validateAllTab() {
			let valid = false;
			for (let i = 0; i <= 4; i++) {
				this.activeTab = i;
				valid = await this.validateTab(i);
				if (!valid) {
					break;
				}
			}
			return valid;
		}

		async validateTab(tab) {
			let valid = false;
			if (tab == 0) {
				valid = await this.$refs['tabTravelPackage'].validate();
			}
			if (tab == 1) {
				valid = await this.$refs['tabAccommodationPackage'].validate();
			}
			if (tab == 2) {
				valid = await this.$refs['tabTransportPackage'].validate();
			}
			if (tab == 3) {
				valid = await this.$refs['tabExtraServicePackage'].validate();
			}
			if (tab == 4) {
				valid = await this.$refs['tabActivityPackage'].validate();
			}
			if (!valid) {
				this.activeTab = tab;
			}
			return valid;
		}

		get isDisabledTab() {
			return !this.travel_id;
		}

		setTravelId(travel_id) {
			this.travel_id = travel_id;
		}

		setPrice(value, type) {
			if (type == 'accommodation_price') {
				this.total[type] = value * (this.package.days - 1);
			} else {
				this.total[type] = value;
			}
		}

		// N-288 · 4A: «Actualizando los precios para N pasajeros…», o '' si no hay que esperar.
		get textoDeEspera() {
			return textoDeEspera(this.esperaDePrecios, this.people);
		}

		get sumTodal() {
			let sum = 0;
			for (const el in this.total) {
				if (Object.prototype.hasOwnProperty.call(this.total, el)) {
					sum += parseFloat(this.total[el]);
				}
			}
			return FormatHelper.formatPrice(sum);
		}

		setTravelPackage(packageData) {
			this.package.name = packageData.name;
			this.package.description = packageData.description;
			this.package.img_url = packageData.img_url;
			this.package.status_id = packageData.status_id;
			this.package.travel_id = packageData.travel_id;
			this.package.days = packageData.days;
			this.package.offer = packageData.offer;
			this.package.offer_start_date = packageData.offer_start_date;
			this.package.offer_end_date = packageData.offer_end_date;
			this.package.offer_amount = packageData.offer_amount;
			this.package.people = packageData.people;
			this.package.text_1 = packageData.text_1;
			this.package.text_2 = packageData.text_2;
			this.package.text_3 = packageData.text_3;
			this.people = packageData.people;
		}
	}
